import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { api } from '@/lib/api';

export default function SuperErrorsPage() {
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['super-errors'],
    queryFn: async () => {
      const r = await api('/api/v1/super/errors?open=1');
      if (r.status !== 200) throw new Error('errors');
      return r.data.errors ?? [];
    },
    refetchInterval: 15_000,
  });

  const resolve = useMutation({
    mutationFn: async (id) => {
      const r = await api(`/api/v1/super/errors/${id}`, { method: 'PATCH', body: {} });
      if (r.status !== 200) throw new Error('resolve failed');
    },
    onSuccess: () => {
      toast.success('Marked resolved');
      qc.invalidateQueries({ queryKey: ['super-errors'] });
    },
    onError: () => toast.error('Resolve failed'),
  });

  const columns = useMemo(
    () => [
      {
        key: 'createdAt',
        label: 'When',
        render: (v) => new Date(String(v)).toLocaleString(),
      },
      {
        key: 'severity',
        label: 'Severity',
        render: (v) => <StatusPill status={v} />,
      },
      { key: 'source', label: 'Source' },
      {
        key: 'code',
        label: 'Code',
        render: (v) => String(v ?? '—'),
        className: 'font-mono text-xs',
      },
      { key: 'message', label: 'Message' },
      {
        key: 'route',
        label: 'Route',
        render: (v) => String(v ?? '—'),
        className: 'font-mono text-xs',
      },
      { key: 'companyId', label: 'Company', className: 'font-mono text-xs' },
      {
        key: 'actions',
        label: '',
        render: (_v, row) => (
          <Button
            size="sm"
            variant="outline"
            disabled={resolve.isPending}
            onClick={() => resolve.mutate(row.id)}
          >
            Resolve
          </Button>
        ),
      },
    ],
    [resolve]
  );

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Error tracer"
          description="API failures, logic breaks, and client crashes — secrets redacted."
          icon="Bug"
          metrics={[
            { label: 'Open', value: q.data?.length ?? 0 },
            {
              label: 'Logic',
              value: q.data?.filter((e) => e.source === 'logic').length ?? 0,
            },
          ]}
        />
        <Card>
          <CardContent className="pt-6">
            <DataTable
              columns={columns}
              data={q.data ?? []}
              emptyMessage="No open errors"
            />
          </CardContent>
        </Card>
      </div>
);
}
